import type { TOC } from '@ember/component/template-only';
import { fn, hash } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import GeneralButtonsButtonsPreviousNextSmall from 'lumen/components/general/buttons/buttons-previous-next-small';
import GeneralCalendarsCalendarRange from 'lumen/components/general/calendars/calendar-range';
import GeneralDatepickersFieldsDropdownsMonthRange from 'lumen/components/general/datepickers/fields/dropdowns-month-range';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDateRange from 'lumen/components/general/dropdowns/triggers/trigger-date-range';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';

interface GeneralDatepickersDatepickerSharedTemplateSignature {
  Args: {
    applyChanges: unknown;
    beginReached: unknown;
    calendarFocusOnEnd: unknown;
    canSelectMonth: unknown;
    cancelChanges: unknown;
    dateTooltips: unknown;
    disabledTooltipMessage: unknown;
    displayEvents: unknown;
    endReached: unknown;
    footerMessage: unknown;
    isApplyDisabled: unknown;
    isDisabled: unknown;
    maxDate: unknown;
    maxRange: unknown;
    minDate: unknown;
    nextClicked: unknown;
    onCancel: unknown;
    previousClicked: unknown;
    range: unknown;
    selectRange: unknown;
    showMonthDropdowns: unknown;
    standardRange: unknown;
    tempRange: unknown;
    triggerClass: unknown;
    triggerLabel: unknown;
    triggerLabelFormatter: unknown;
    updateTempRange: unknown;
  };
  Blocks: {
    beforeContent: [];
    sidebar: [];
  };
}

const GeneralDatepickersDatepickerSharedTemplate: TOC<GeneralDatepickersDatepickerSharedTemplateSignature> =
  <template>
    <div
      class="flex-date-picker-container layout-row flex items-center justify-start"
      {{! pass null here explicitly, otherwise the element gets passed as optionId which causes an issue with the action }}
      {{! @glint-expect-error: Incorrect type }}
      {{didInsert (fn @updateTempRange @range null)}}
      {{! @glint-expect-error: Incorrect type }}
      {{didUpdate (fn @updateTempRange @range null) @standardRange}}
      {{! @glint-expect-error: Incorrect type }}
      {{didUpdate (fn @updateTempRange @range null) @range}}
    >
      {{#if (and @isDisabled @disabledTooltipMessage)}}
        {{! @glint-expect-error: Incorrect type }}
        <PrismTooltip @text={{@disabledTooltipMessage}} />
      {{/if}}

      <div class="mr-300">
        <GeneralButtonsButtonsPreviousNextSmall
          {{! @glint-expect-error: Incorrect type }}
          @beginReached={{@beginReached}}
          {{! @glint-expect-error: Incorrect type }}
          @endReached={{@endReached}}
          {{! @glint-expect-error: Incorrect type }}
          @isDisabled={{@isDisabled}}
          {{! @glint-expect-error: Incorrect type }}
          @nextClicked={{@nextClicked}}
          {{! @glint-expect-error: Incorrect type }}
          @previousClicked={{@previousClicked}}
        />
      </div>
      <GeneralDropdownsDropdownYield
        @extra={{hash
          class=@triggerClass
          isDisabled=@isDisabled
          triggerLabelFormatter=@triggerLabelFormatter
        }}
        @label={{@triggerLabel}}
        {{! @glint-expect-error: Incorrect type }}
        @onCancel={{@onCancel}}
        @selected={{@tempRange}}
        @triggerComponent={{GeneralDropdownsTriggersTriggerDateRange}}
        as |dropdown|
      >
        <section class="date-picker-with-side-bar-calendar">
          <section class="date-picker-with-side-bar-calendar__content">
            <div class="mb-500">
              {{yield to="beforeContent"}}

              {{#if @showMonthDropdowns}}
                <GeneralDatepickersFieldsDropdownsMonthRange
                  {{! @glint-expect-error: Incorrect type }}
                  @maxDate={{@maxDate}}
                  @maxRange={{@maxRange}}
                  @minDate={{@minDate}}
                  @selectRange={{@selectRange}}
                  @selectedRange={{@tempRange}}
                />
              {{/if}}
              <GeneralCalendarsCalendarRange
                @calendarFocusOnEnd={{@calendarFocusOnEnd}}
                {{! @glint-expect-error: Incorrect type }}
                @canSelectMonth={{@canSelectMonth}}
                @dateTooltips={{@dateTooltips}}
                {{! @glint-expect-error: Incorrect type }}
                @displayEvents={{@displayEvents}}
                {{! @glint-expect-error: Incorrect type }}
                @highlightPeriod={{this.highlightPeriod}}
                {{! @glint-expect-error: Incorrect type }}
                @isDisabled={{@showMonthDropdowns}}
                {{! @glint-expect-error: Incorrect type }}
                @maxDate={{@maxDate}}
                @maxRange={{@maxRange}}
                {{! @glint-expect-error: Incorrect type }}
                @minDate={{@minDate}}
                {{! @glint-expect-error: Incorrect type }}
                @refocusMonths={{@refocusMonths}}
                {{! @glint-expect-error: Incorrect type }}
                @selectRange={{if @showMonthDropdowns null @selectRange}}
                {{! @glint-expect-error: Incorrect type }}
                @selectedRange={{@tempRange}}
                @standardRange={{@standardRange}}
              />
              {{! @glint-expect-error: Incorrect type }}
              {{#if (and @displayEvents this.access.hasEvents)}}
                <div class="legend-container text-200--regular">
                  <div class="holiday-or-event-indicator"></div>
                  {{t "revenue_insight.holiday_or_event"}}
                </div>
              {{/if}}
            </div>

            {{yield to="sidebar"}}
          </section>
          <section class="date-picker-with-side-bar-calendar__footer">
            {{#if @footerMessage}}
              <div
                class="text-200--regular mr-500 flex flex-row items-center gap-100"
              >
                <PrismIcon
                  @name="circle-information-outline"
                  @size="extra-small"
                />
                {{! @glint-expect-error: Incorrect type }}
                {{@footerMessage}}
              </div>
            {{/if}}
            <div class="datepicker-save-cancel">
              <GeneralFormFieldsCancelAndConfirm
                {{! @glint-expect-error: Incorrect type }}
                @cancel={{fn @cancelChanges dropdown}}
                {{! @glint-expect-error: Incorrect type }}
                @confirm={{fn @applyChanges dropdown}}
                {{! @glint-expect-error: Incorrect type }}
                @isConfirmDisabled={{@isApplyDisabled}}
              />
            </div>
          </section>
        </section>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>;

export default GeneralDatepickersDatepickerSharedTemplate;
